// Listening progress (spec §3 ListenEvent): one record per playback session, sent every 15 s
// while playing and immediately on pause / end / page hide. Unsent events survive in
// localStorage and are retried; the server upserts by sessionId, so resending is harmless.
import { LISTEN_BATCH_MAX, type ListenEventInput } from '@fonus/shared';
import { api } from '../api/client.ts';

const STORAGE_KEY = 'fonus-ertak-listen-queue';

const pending = new Map<string, ListenEventInput>(load());
let flushing: Promise<void> | null = null;

function load(): [string, ListenEventInput][] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list = raw ? (JSON.parse(raw) as ListenEventInput[]) : [];
    return list.map((e) => [e.sessionId, e]);
  } catch {
    return [];
  }
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...pending.values()]));
  } catch {
    // storage full / disabled: events are best-effort
  }
}

/** Records the latest state of a session (only the newest per session is kept). */
export function track(event: ListenEventInput): void {
  pending.set(event.sessionId, event);
  save();
}

/** Sends everything queued. `keepalive` lets it finish while the page is closing. */
export function flush(opts: { keepalive?: boolean } = {}): Promise<void> {
  if (flushing || pending.size === 0) return flushing ?? Promise.resolve();
  const batch = [...pending.values()].slice(0, LISTEN_BATCH_MAX);
  flushing = api('/listen/events', {
    method: 'POST',
    body: { events: batch },
    ...(opts.keepalive && { keepalive: true }),
  })
    .then(() => {
      // Drop only what was sent and not updated meanwhile.
      for (const e of batch) {
        if (pending.get(e.sessionId) === e) pending.delete(e.sessionId);
      }
      save();
    })
    .catch(() => undefined)
    .finally(() => {
      flushing = null;
    });
  return flushing;
}

export function pendingCount(): number {
  return pending.size;
}
